import React, { useMemo } from "react";
import "../styles/HowItWorksPage.css";
import { usePayment } from "../context/PaymentContext";

const HowItWorksPage = () => {
    const { selectedCurrency, fiatRates } = usePayment();
    // Символ валюты
    function getCurrencySymbol(currency) {
        switch (currency) {
            case 'USD': return '$';
            case 'RUB': return '₽';
            case 'BRL': return 'R$';
            case 'USDT': return '₮';
            case 'EUR': return '€';
            default: return '';
        }
    }
    // Пересчёт RUB в выбранную валюту
    function convertRub(value) {
        if (selectedCurrency === 'RUB') return value;
        if (selectedCurrency === 'USD' && fiatRates.RUB_USD) return Math.round(value * fiatRates.RUB_USD);
        if (selectedCurrency === 'BRL' && fiatRates.RUB_BRL) return Math.round(value * fiatRates.RUB_BRL);
        if (selectedCurrency === 'USDT' && fiatRates.RUB_USDT) return Math.round(value * fiatRates.RUB_USDT);
        if (selectedCurrency === 'EUR' && fiatRates.RUB_EUR) return Math.round(value * fiatRates.RUB_EUR);
        return value;
    }
    // --- Таблицы ---
    const cashbackTable = useMemo(() => [
        { min: convertRub(50000000), percent: 30, max: convertRub(50000) },
        { min: convertRub(20000000), percent: 20, max: convertRub(30000) },
        { min: convertRub(10000000), percent: 10, max: convertRub(20000) },
        { min: convertRub(1000000), percent: 5, max: convertRub(15000) },
        { min: convertRub(800000), percent: 4, max: convertRub(8000) },
        { min: convertRub(500000), percent: 3, max: convertRub(5000) },
        { min: convertRub(300000), percent: 2, max: convertRub(4000) },
        { min: convertRub(100000), percent: 1, max: convertRub(3000) },
    ], [selectedCurrency, fiatRates]);
    const bonusCashbackTable = useMemo(() => [
        { min: convertRub(1000000), percent: 20 },
        { min: convertRub(700000), percent: 10 },
        { min: convertRub(500000), percent: 5 },
        { min: convertRub(100000), percent: 4 },
        { min: convertRub(50000), percent: 3 },
        { min: convertRub(20000), percent: 2 },
        { min: convertRub(5000), percent: 1 },
    ], [selectedCurrency, fiatRates]);
    const currencySymbol = getCurrencySymbol(selectedCurrency);
    return (
        <div className="howitworks-container">

            <h1>Кешбек и бонусный счёт</h1>
            <section style={{ marginBottom: 32 }}>
                <h2 style={{ marginTop: 0 }}>Заберите бонус в 500%</h2>
                <p>
                    Бонус зачисляется <b>автоматически</b> после пополнения основного счёта.<br />
                    <ul>
                        <li><b>+200%</b> от суммы 1 депозита</li>
                        <li><b>+150%</b> от суммы 2 депозита</li>
                        <li><b>+100%</b> от суммы 3 депозита</li>
                        <li><b>+50%</b> от суммы 4 депозита</li>
                    </ul>
                </p>
                <p>
                    <b>Пример:</b> Если вы пополнили счёт на {convertRub(10000).toLocaleString()} {currencySymbol}:
                    <ul>
                        <li>1-й депозит: получите бонус {convertRub(20000).toLocaleString()} {currencySymbol} (200%)</li>
                        <li>2-й депозит: получите бонус {convertRub(15000).toLocaleString()} {currencySymbol} (150%)</li>
                        <li>3-й депозит: получите бонус {convertRub(10000).toLocaleString()} {currencySymbol} (100%)</li>
                        <li>4-й депозит: получите бонус {convertRub(5000).toLocaleString()} {currencySymbol} (50%)</li>
                    </ul>
                </p>
            </section>
            <section>
                <h2>Бонусный счёт и бонусный кешбек</h2>
                <p>
                    Бонусный счёт — это ваши бонусные средства, которые можно отыграть и перевести на основной баланс. Для этого действует <b>бонусный кешбек</b>:
                </p>
                <table className="howitworks-table">
                    <thead>
                        <tr>
                            <th>Проигрыш за сутки, {currencySymbol}</th>
                            <th>Процент бонусных средств</th>
                        </tr>
                    </thead>
                    <tbody>
                        {bonusCashbackTable.map((row, i) => (
                            <tr key={i}>
                                <td>от {row.min.toLocaleString()}</td>
                                <td>{row.percent}%</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p>
                    <b>Как это работает:</b>
                </p>
                <ul>
                    <li>Считается проигрыш за последние сутки (24 часа) по всем ставкам.</li>
                    <li>Вы можете перевести на основной счёт не больше, чем есть на бонусном балансе.</li>
                    <li>После перевода бонусы списываются с бонусного счёта и зачисляются на основной.</li>
                    <li>Забирать бонусный кешбек можно раз в 24 часа.</li>
                </ul>
                <p>
                    <b>Пример:</b> У вас бонусный баланс {convertRub(10000).toLocaleString()} {currencySymbol}, за сутки проиграли {convertRub(100000).toLocaleString()} {currencySymbol}.<br />
                    Вы получите 4% от {convertRub(100000).toLocaleString()} = {convertRub(4000).toLocaleString()} {currencySymbol} (но не больше, чем есть на бонусном счёте). После этого на бонусном счёте останется {convertRub(6000).toLocaleString()} {currencySymbol}, а на основном прибавится {convertRub(4000).toLocaleString()} {currencySymbol}.
                </p>
            </section>
            <section>
                <h2>Еженедельный кешбек</h2>
                <p>
                    Каждую неделю вы автоматически получаете кешбек за свои ставки. Размер кешбека зависит от суммы проигрыша за неделю и рассчитывается по следующей таблице:
                </p>
                <table className="howitworks-table">
                    <thead>
                        <tr>
                            <th>Проигрыш за неделю, {currencySymbol}</th>
                            <th>Процент кешбека</th>
                            <th>Максимум, {currencySymbol}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {cashbackTable.map((row, i) => (
                            <tr key={i}>
                                <td>от {row.min.toLocaleString()}</td>
                                <td>{row.percent}%</td>
                                <td>{row.max.toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p>
                    <b>Пример:</b> Если за неделю вы проиграли {convertRub(1200000).toLocaleString()} {currencySymbol}, вы получите 5% кешбека, но не более {convertRub(15000).toLocaleString()} {currencySymbol}.
                </p>
                <ul>
                    <li><b>🕐 Автоматическое начисление</b> — кешбек начисляется автоматически каждую пятницу в 21:00 UTC.</li>
                    <li><b>📊 Период расчета</b> — считаются ставки с прошлой пятницы до текущей пятницы.</li>
                    <li><b>💰 Мгновенно на баланс</b> — кешбек сразу поступает на ваш основной счет.</li>
                    <li><b>🔄 Непрерывный цикл</b> — новый период начинается сразу после начисления.</li>
                </ul>
            </section>

            <section>
                <h2>Вопросы и ответы</h2>
                <ul>
                    <li><b>Когда начисляется кешбек?</b> — Автоматически каждую пятницу в 21:00 UTC.</li>
                    <li><b>Когда можно забрать бонусный кешбек?</b> — Раз в 24 часа, если есть проигрыш и бонусный баланс.</li>
                    <li><b>Что если я не играл неделю?</b> — Кешбек начисляется только при наличии ставок.</li>
                    <li><b>Можно ли получить больше, чем есть на бонусном счёте?</b> — Нет, лимит — ваш бонусный баланс.</li>
                    <li><b>Нужно ли что-то делать для получения кешбека?</b> — Нет, все происходит автоматически.</li>
                </ul>
            </section>
        </div>
    );
};

export default HowItWorksPage; 